import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from "react-router-dom";
import apiPath from "../../constants/apiPath";
import Http from "../../services/Http";
import { CART_DRAWER_OPEN, notifyCartUpdated } from "../../services/cartDrawerEvents";
import Currency from './Currency';

const FREE_SHIPPING_THRESHOLD = 15;

const getCurrentCurrency = () => {
    try {
        return localStorage.getItem("current_currency")
            ? JSON.parse(localStorage.getItem("current_currency"))
            : { country_code: "KWD" };
    } catch (error) {
        return { country_code: "KWD" };
    }
};

const CartDrawer = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [actionId, setActionId] = useState(null);
    const [cartData, setCartData] = useState(null);

    const items = useMemo(() => {
        return Array.isArray(cartData?.items) ? cartData.items : [];
    }, [cartData]);

    const subtotal = useMemo(() => {
        return items.reduce((total, item) => {
            const lineTotal = parseFloat(item.amount) || 0;
            const price = parseFloat(item.product_price) || 0;
            const qty = parseInt(item.qty, 10) || 0;
            return total + (lineTotal > 0 ? lineTotal : (price * qty));
        }, 0);
    }, [items]);

    const progress = Math.min((subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100);
    const remaining = Math.max(FREE_SHIPPING_THRESHOLD - subtotal, 0);
    const isRtl = typeof document !== 'undefined' && document.documentElement.getAttribute('dir') === 'rtl';
    const progressIconPosition = isRtl ? { right: `${progress}%` } : { left: `${progress}%` };

    const fetchCart = useCallback(async (shouldNotify = false) => {
        setIsLoading(true);
        const data = new FormData();
        const currentCurrency = getCurrentCurrency();

        data.append('delivery_type', 'delivery');
        data.append('latitude', 26.8587263);
        data.append('longitude', 75.7720215);
        data.append('currency', currentCurrency.country_code || 'KWD');

        const result = await Http(apiPath.getCart, {
            method: 'POST',
            data
        });

        if (result?.status === 200 && result.data?.status) {
            const nextCartData = {
                ...result.data.data,
                items: Array.isArray(result.data.data?.items)
                    ? result.data.data.items
                    : []
            };

            setCartData(nextCartData);
            if (shouldNotify) {
                notifyCartUpdated(nextCartData);
            }
        }

        setIsLoading(false);
    }, []);

    const updateItemQty = async (item, nextQty) => {
        if (nextQty < 1 || !item.products) {
            return;
        }

        setActionId(item.id);
        const data = new FormData();
        data.append('product_id', item.products.id);
        data.append('qty', nextQty);

        if (item.products.attribute_value_id) {
            data.append('product_attribute_values_id', item.products.attribute_value_id);
        }

        const result = await Http(item.type === 'Product' ? apiPath.AddtoCart : apiPath.addToCombo, {
            method: 'POST',
            data,
            skipCartDrawer: true
        });

        if (result?.status === 200 && result.data?.status) {
            await fetchCart(true);
        }

        setActionId(null);
    };

    const removeItem = async (item) => {
        setActionId(item.id);
        const data = new FormData();
        data.append('cart_id', item.id);

        const result = await Http(apiPath.cartDestroy, {
            method: 'POST',
            data
        });

        if (result?.status === 200 && result.data?.status) {
            // Removing the last item also deletes the cart parent, so get-cart
            // returns status:false and fetchCart can't refresh. cartDestroy's own
            // response carries the remaining cart parents ([] when empty) — use it
            // to clear the drawer in that case; otherwise re-fetch for fresh totals.
            const remaining = result.data.data;
            if (Array.isArray(remaining) && remaining.length === 0) {
                const emptyCart = { items: [] };
                setCartData(emptyCart);
                notifyCartUpdated(emptyCart);
            } else {
                await fetchCart(true);
            }
        }

        setActionId(null);
    };

    useEffect(() => {
        const handleOpen = () => {
            setIsOpen(true);
            fetchCart();
        };

        window.addEventListener(CART_DRAWER_OPEN, handleOpen);

        return () => {
            window.removeEventListener(CART_DRAWER_OPEN, handleOpen);
        };
    }, [fetchCart]);

    useEffect(() => {
        document.body.classList.toggle('cart-drawer-lock', isOpen);

        return () => {
            document.body.classList.remove('cart-drawer-lock');
        };
    }, [isOpen]);

    const closeDrawer = () => {
        setIsOpen(false);
        // Remove the scroll lock synchronously. "View Cart" navigates in the same
        // click, and if the lock (body{overflow:hidden}) is only cleared later by
        // the effect, the cart page's scrollTo(0,0) runs against a locked body
        // (esp. on mobile) and the screen stays stuck at the previous scroll
        // position (bottom).
        document.body.classList.remove('cart-drawer-lock');
    };

    return (
        <>
            <div className={`cart-drawer-backdrop ${isOpen ? 'show' : ''}`} onClick={closeDrawer}></div>
            <aside className={`cart-drawer ${isOpen ? 'show' : ''}`} aria-hidden={!isOpen}>
                <div className="cart-drawer-header">
                    <h3>Shopping Cart ({items.length})</h3>
                    <button type="button" className="cart-drawer-close" onClick={closeDrawer} aria-label="Close cart">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="cart-drawer-freeship">
                    <div className="cart-drawer-progress">
                        <span className="cart-drawer-progress-fill" style={{ width: `${progress}%` }}></span>
                        <span className="cart-drawer-progress-icon" style={progressIconPosition}>
                            <i className="fas fa-truck"></i>
                        </span>
                    </div>
                    <p>
                        {remaining > 0 ? (
                            <>Spend {remaining.toFixed(3)} KWD more to enjoy <strong>Free shipping!</strong></>
                        ) : (
                            <><strong>Free shipping unlocked!</strong></>
                        )}
                    </p>
                </div>

                <div className="cart-drawer-body">
                    {isLoading ? (
                        <div className="cart-drawer-state">Loading cart...</div>
                    ) : items.length > 0 ? (
                        items.map((item) => (
                            <div className="cart-drawer-item" key={`${item.type}-${item.id}`}>
                                <div className="cart-drawer-item-img">
                                    {item.products?.image && <img src={item.products.image} alt={item.products.name || 'Product'} />}
                                </div>
                                <div className="cart-drawer-item-info">
                                    <h4>{item.products?.name || 'Product not available'}</h4>
                                    {item.products?.attribute_value && <p>Size: {item.products.attribute_value}</p>}
                                    <div className="cart-drawer-price-row">
                                        <strong><Currency data={item.amount > 0 ? item.amount : item.product_price} /></strong>
                                        {item.amount > 0 && item.product_price !== item.amount && (
                                            <span><Currency data={item.product_price} /></span>
                                        )}
                                    </div>
                                    <div className="cart-drawer-actions">
                                        {!item.is_sample && (
                                            <div className="cart-drawer-qty-control">
                                                <button
                                                    type="button"
                                                    onClick={() => updateItemQty(item, (parseInt(item.qty, 10) || 1) - 1)}
                                                    disabled={actionId === item.id || item.qty <= 1}
                                                    aria-label="Decrease quantity"
                                                >
                                                    -
                                                </button>
                                                <input type="number" value={item.qty} readOnly />
                                                <button
                                                    type="button"
                                                    onClick={() => updateItemQty(item, (parseInt(item.qty, 10) || 1) + 1)}
                                                    disabled={actionId === item.id}
                                                    aria-label="Increase quantity"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="cart-drawer-delete"
                                            onClick={() => removeItem(item)}
                                            disabled={actionId === item.id}
                                            aria-label="Remove item"
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="cart-drawer-state">Your cart is empty.</div>
                    )}
                </div>

                <div className="cart-drawer-footer">
                    <div className="cart-drawer-subtotal">
                        <span>Subtotal</span>
                        <strong><Currency data={subtotal} /></strong>
                    </div>
                    <Link to="/cart" className="cart-drawer-view-cart" onClick={closeDrawer}>
                        View Cart
                    </Link>
                </div>
            </aside>
        </>
    );
};

export default CartDrawer;
